import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { CatalogEntities } from '@/types/learning/learning-catalog';
import { HookResult } from './apolloClient';
import {
  RCatalogCardType,
  RLearningPinnedFragmentFragment,
  useLearningPinnedUpdateMutationRemote,
  useLearningsPinnedQueryRemote,
} from '@/store/v2';

export interface LearningPinnedUpdateInput {
  realm: string;
  entity: CatalogEntities;
  entityId: number;
}

export interface LearningPinned {
  realm: string;
  learningId: number | null;
  learningPathId: number | null;
  learningJourneyId: number | null;
  learningCollectionId: number | null;
  position: number;
}

const LEARNINGS_PINNED_QUERY_OPERATION_NAME = `learningsPinned`;

/** `CatalogEntities` and the generated `RCatalogCardType` share values but are distinct TS types. */
const REMOTE_CATALOG_ENTITY: Record<CatalogEntities, RCatalogCardType> = {
  product: RCatalogCardType.Product,
  learning: RCatalogCardType.Learning,
  collection: RCatalogCardType.Collection,
  journey: RCatalogCardType.Journey,
};

/** Every id on LearningPinned is nullable in the schema; the domain type spells that as null. */
const toLearningPinned = (pinned: RLearningPinnedFragmentFragment): LearningPinned => ({
  realm: pinned.realm,
  learningId: pinned.learningId ?? null,
  learningPathId: pinned.learningPathId ?? null,
  learningJourneyId: pinned.learningJourneyId ?? null,
  learningCollectionId: pinned.learningCollectionId ?? null,
  position: pinned.position,
});

interface LearningPinnedResponse extends HookResult {
  learningsPinned: readonly LearningPinned[];
  refetch: () => Promise<ApolloQueryResult<{ learningsPinned: ReadonlyArray<LearningPinned> }>>;
}

export const useLearningPinned = (realm: string, skip?: boolean): LearningPinnedResponse => {
  const { data, loading, refetch, error, networkStatus } = useLearningsPinnedQueryRemote({
    variables: { realm },
    skip,
  });

  const learningsPinned = useMemo<ReadonlyArray<LearningPinned>>(
    () => (data?.learningsPinned ?? []).map(toLearningPinned),
    [data],
  );

  const refetchMapped = useCallback(async () => {
    const result = await refetch();

    // errorPolicy is 'all', so a failed refetch resolves with no data rather than rejecting.
    return { ...result, data: { learningsPinned: (result.data?.learningsPinned ?? []).map(toLearningPinned) } };
  }, [refetch]);

  return {
    learningsPinned,
    loading,
    refetch: refetchMapped,
    error,
    networkStatus,
  };
};

interface LearningPinnedUpdateResponse {
  learningPinnedUpdate: (
    realm: string,
    input: LearningPinnedUpdateInput[],
  ) => Promise<FetchResult<{ learningPinnedUpdate: LearningPinned[] }>>;
  loading: boolean;
}

export const useLearningPinnedUpdate = (): LearningPinnedUpdateResponse => {
  const [learningPinnedUpdate, { loading }] = useLearningPinnedUpdateMutationRemote();

  return {
    learningPinnedUpdate: async (realm: string, input: LearningPinnedUpdateInput[]) => {
      const result = await learningPinnedUpdate({
        variables: {
          realm,
          input: input.map((item) => ({
            realm: item.realm,
            entity: REMOTE_CATALOG_ENTITY[item.entity],
            entityId: item.entityId,
          })),
        },
        refetchQueries: [LEARNINGS_PINNED_QUERY_OPERATION_NAME],
      });

      return {
        ...result,
        data: result.data ? { learningPinnedUpdate: result.data.learningPinnedUpdate.map(toLearningPinned) } : undefined,
      };
    },
    loading,
  };
};
